import React, { useState } from "react";
import { Calendar, Check, ChevronDown, Filter, RotateCcw, X } from "lucide-react";
import { useTranslation, useFormat } from "../../i18n";
import { Segmented } from "./primitives";

const ROLLING = ["7d", "14d", "30d", "90d", "180d", "365d"];
const CALENDAR = ["wtd", "mtd", "qtd", "ytd", "last_week", "last_month", "last_quarter", "last_year"];

const PresetMenu = ({ filters, setFilters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(filters.startDate || "");
  const [draftEnd, setDraftEnd] = useState(filters.endDate || "");
  const isCustom = Boolean(filters.startDate && filters.endDate);

  const current = isCustom
    ? `${fmt.date(new Date(`${filters.startDate}T00:00:00`), { month: "short", day: "numeric" })} – ${fmt.date(new Date(`${filters.endDate}T00:00:00`), { month: "short", day: "numeric", year: "numeric" })}`
    : t(`presets.${filters.preset}`);

  const choose = (preset) => {
    setFilters({ preset, startDate: "", endDate: "" });
    setOpen(false);
  };
  const applyCustom = () => {
    if (!draftStart || !draftEnd || draftStart > draftEnd) return;
    setFilters({ preset: "custom", startDate: draftStart, endDate: draftEnd });
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Calendar className="w-4 h-4 text-purple-600" aria-hidden="true" />
        <span className="truncate max-w-[200px]">{current}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute z-40 mt-2 w-[300px] sm:w-[360px] bg-white rounded-2xl border border-slate-200 shadow-xl p-2 left-0" role="menu">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t("presets.rolling")}</p>
                {ROLLING.map((p) => (
                  <button key={p} type="button" role="menuitemradio" aria-checked={!isCustom && filters.preset === p} onClick={() => choose(p)} className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
                    <span>{t(`presets.${p}`)}</span>
                    {!isCustom && filters.preset === p && <Check className="w-4 h-4 text-purple-600" strokeWidth={3} aria-hidden="true" />}
                  </button>
                ))}
              </div>
              <div>
                <p className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t("presets.calendar")}</p>
                {CALENDAR.map((p) => (
                  <button key={p} type="button" role="menuitemradio" aria-checked={!isCustom && filters.preset === p} onClick={() => choose(p)} className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
                    <span>{t(`presets.${p}`)}</span>
                    {!isCustom && filters.preset === p && <Check className="w-4 h-4 text-purple-600" strokeWidth={3} aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-100 mt-2 pt-2 px-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{t("presets.custom")}</p>
              <div className="flex items-center gap-2">
                <input type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} className="flex-1 min-w-0 px-2 py-1 border border-slate-300 rounded-lg text-xs" aria-label={t("start")} />
                <span className="text-slate-400 text-xs">–</span>
                <input type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} className="flex-1 min-w-0 px-2 py-1 border border-slate-300 rounded-lg text-xs" aria-label={t("end")} />
                <button type="button" onClick={applyCustom} disabled={!draftStart || !draftEnd || draftStart > draftEnd} className="px-2.5 py-1 rounded-full bg-purple-600 text-white text-xs font-medium disabled:opacity-40">
                  {t("apply")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Select = ({ value, onChange, options, ariaLabel, allLabel }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 shadow-sm max-w-[180px] truncate focus:outline-none focus:ring-2 focus:ring-purple-500">
    <option value="all">{allLabel}</option>
    {options.map((o) => (
      <option key={o.id} value={o.id}>
        {o.label}
      </option>
    ))}
  </select>
);

/**
 * The one filter row every analytics panel reads from.
 * Options (teams, types) come from the /filters endpoint via `options`.
 */
const FilterBar = ({ filters, setFilters, reset, options, showTeam = true, showType = true, showGranularity = true, showCompare = true, children }) => {
  const { t } = useTranslation("analytics");
  const teams = options?.teams || [];
  const types = (options?.recognition_types || []).filter((x) => x.is_active || x.id === filters.recognitionTypeId);
  const active = [filters.teamId !== "all", filters.recognitionTypeId !== "all", Boolean(filters.granularity), filters.compare === false].filter(Boolean).length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-2.5 sm:p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 mr-1">
          <Filter className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">{t("filters")}</span>
        </span>
        <PresetMenu filters={filters} setFilters={setFilters} />
        {showTeam && (
          <Select value={filters.teamId} onChange={(v) => setFilters({ teamId: v })} options={teams.map((x) => ({ id: x.id, label: x.name }))} ariaLabel={t("team_label")} allLabel={t("all_teams")} />
        )}
        {showType && (
          <Select
            value={filters.recognitionTypeId}
            onChange={(v) => setFilters({ recognitionTypeId: v })}
            options={types.map((x) => ({ id: x.id, label: `${x.emoji && !String(x.emoji).startsWith("/") ? `${x.emoji} ` : ""}${x.name}` }))}
            ariaLabel={t("recognition_type")}
            allLabel={t("all_types")}
          />
        )}
        {showGranularity && (
          <Segmented
            ariaLabel={t("granularity.label")}
            value={filters.granularity || "auto"}
            onChange={(v) => setFilters({ granularity: v === "auto" ? "" : v })}
            options={[
              { value: "auto", label: t("granularity.auto") },
              { value: "day", label: t("granularity.day") },
              { value: "week", label: t("granularity.week") },
              { value: "month", label: t("granularity.month") },
            ]}
          />
        )}
        {showCompare && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none px-1">
            <input type="checkbox" checked={filters.compare !== false} onChange={(e) => setFilters({ compare: e.target.checked })} className="rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
            {t("compare_previous")}
          </label>
        )}
        <div className="flex-1" />
        {children}
        {active > 0 && (
          <button type="button" onClick={reset} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-500 hover:bg-slate-100" title={t("reset_filters")}>
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">{t("reset_filters")}</span>
            <span className="sm:hidden">
              <X className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
